import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Container,
    Typography,
    Box,
    Card,
    CardContent,
    Grid,
    CircularProgress,
    Alert,
    Breadcrumbs,
    Link,
    Chip,
    useTheme
} from '@mui/material';
import {
    ElectricBolt as HeatPumpIcon,
    Home as InsulationIcon,
    SolarPower as PVIcon,
    BatteryChargingFull as BESSIcon,
    Window as WindowIcon,
    Home as HomeIcon,
    AcUnit as AirConditionerIcon,
    Lightbulb as LightingIcon,
    Kitchen as AppliancesIcon
} from '@mui/icons-material';
import ReactECharts from 'echarts-for-react';

const BuildingOptimization = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id } = useParams();

    const [optimizationData, setOptimizationData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const fetchOptimizationResults = async () => {
            try {
                const response = await axios.get(`/buildings/${id}/optimization-results/`);
                setOptimizationData(response.data);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching optimization results:', err);
                setError(true);
                setLoading(false);
            }
        };

        fetchOptimizationResults();
    }, [id]);

    const optimizationCategories = [
        {
            key: 'heating_system',
            name: 'Heat Pump System',
            icon: <HeatPumpIcon sx={{ fontSize: '2.2rem' }} />,
            color: '#1976d2',
            description: 'High-efficiency heat pump system recommendations to replace conventional heating.'
        },
        {
            key: 'insulation',
            name: 'Insulation',
            icon: <InsulationIcon sx={{ fontSize: '2.2rem' }} />,
            color: '#388e3c',
            description: 'Thermal insulation solutions to improve building envelope performance.'
        },
        {
            key: 'windows',
            name: 'Windows',
            icon: <WindowIcon sx={{ fontSize: '2.2rem' }} />,
            color: '#d32f2f',
            description: 'Advanced glazing solutions to minimize heat transfer.'
        },
        {
            key: 'pv_system',
            name: 'Solar PV System',
            icon: <PVIcon sx={{ fontSize: '2.2rem' }} />,
            color: '#f57c00',
            description: 'Photovoltaic system recommendations for clean energy generation.'
        },
        {
            key: 'air_conditioners',
            name: 'Air Conditioners',
            icon: <AirConditionerIcon sx={{ fontSize: '2.2rem' }} />,
            color: '#7b1fa2',
            description: 'Energy-efficient air conditioning system recommendations.'
        },
        {
            key: 'lighting',
            name: 'Lighting',
            icon: <LightingIcon sx={{ fontSize: '2.2rem' }} />,
            color: '#ff9800',
            description: 'LED lighting solutions to reduce energy consumption.'
        },
        {
            key: 'appliances',
            name: 'Appliances',
            icon: <AppliancesIcon sx={{ fontSize: '2.2rem' }} />,
            color: '#795548',
            description: 'Energy-efficient appliance recommendations.'
        }
    ];

    const getChartOptions = (type, title) => {
        if (!optimizationData?.charts || !optimizationData.charts[type]) return {};

        const { labels, values1, values2 } = optimizationData.charts[type];

        return {
            title: {
                text: title,
                left: 'center',
                textStyle: {
                    fontSize: 16,
                    fontWeight: 'bold'
                }
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow'
                },
                formatter: function (params) {
                    let result = params[0].name + '<br/>';
                    params.forEach(param => {
                        const value = type === 'costs' ? `€${param.value}` :
                            type === 'emissions' ? `${param.value} kg CO₂` :
                                `${param.value} kWh`;
                        result += `${param.marker} ${param.seriesName}: ${value}<br/>`;
                    });
                    return result;
                }
            },
            legend: {
                data: ['Before Optimization', 'After Optimization'],
                bottom: 10
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '15%',
                top: '15%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: labels.map(label => label.charAt(0).toUpperCase() + label.slice(1)),
                axisLabel: {
                    rotate: 45
                }
            },
            yAxis: {
                type: 'value',
                name: type === 'costs' ? 'Cost (€)' :
                    type === 'emissions' ? 'Emissions (kg CO₂)' :
                        'Energy (kWh)'
            },
            series: [
                {
                    name: 'Before Optimization',
                    type: 'bar',
                    data: values1,
                    itemStyle: {
                        color: '#ff6b6b'
                    }
                },
                {
                    name: 'After Optimization',
                    type: 'bar',
                    data: values2,
                    itemStyle: {
                        color: '#4ecdc4'
                    }
                }
            ]
        };
    };

    const renderOptimizationCard = (category) => {
        const data = optimizationData?.decisions?.[category.key];
        if (!data) return null;

        const isHeatingSystem = category.key === 'heating_system';
        const isPvSystem = category.key === 'pv_system';

        return (
            <Grid item xs={12} sm={6} md={4} key={category.key}>
                <Card
                    elevation={6}
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'box-shadow 0.3s, transform 0.2s',
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #ffffff 0%, #f5faff 100%)',
                        '&:hover': {
                            boxShadow: 12,
                            transform: 'translateY(-4px)'
                        }
                    }}
                >
                    <CardContent sx={{ flexGrow: 1, pb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Box sx={{ color: category.color, mr: 2 }}>
                                {category.icon}
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {category.name}
                            </Typography>
                        </Box>

                        {isHeatingSystem ? (
                            <>
                                <Box sx={{ mb: 2 }}>
                                    <Chip
                                        label={`${data.matched_capacity} kW`}
                                        color="primary"
                                        size="small"
                                        sx={{ mr: 1 }}
                                    />
                                    <Chip
                                        label={`Efficiency: ${data.efficiency}`}
                                        color="secondary"
                                        size="small"
                                    />
                                </Box>
                                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                                    {category.description}
                                </Typography>
                                {optimizationData?.detailed_costs?.heating_systems > 0 && (
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                        Cost: €{optimizationData.detailed_costs.heating_systems.toLocaleString()}
                                    </Typography>
                                )}
                            </>
                        ) : isPvSystem ? (
                            <>
                                <Typography variant="body1" sx={{ mb: 2, fontWeight: 'medium' }}>
                                    {data}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                    {category.description}
                                </Typography>
                                {optimizationData?.detailed_costs?.pv_system > 0 && (
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                        Cost: €{optimizationData.detailed_costs.pv_system.toLocaleString()}
                                    </Typography>
                                )}
                            </>
                        ) : (
                            <>
                                <Typography variant="body1" sx={{ mb: 2, fontWeight: 'medium' }}>
                                    {data}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                    {category.description}
                                </Typography>
                                {optimizationData?.detailed_costs?.[category.key] > 0 && (
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                        Cost: €{optimizationData.detailed_costs[category.key].toLocaleString()}
                                    </Typography>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        );
    };

    if (loading) {
        return (
            <Container sx={{ mt: 5, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ mt: 2 }}>Loading optimization results...</Typography>
            </Container>
        );
    }

    if (error) {
        return (
            <Container sx={{ mt: 5 }}>
                <Alert severity="error">Failed to load optimization results. Please try again later.</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 5, mb: 5 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
                <Link
                    underline="hover"
                    color="inherit"
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        navigate('/');
                    }}
                    sx={{ display: 'flex', alignItems: 'center' }}
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Home
                </Link>
                <Link
                    underline="hover"
                    color="inherit"
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        navigate('/buildings');
                    }}
                >
                    Buildings
                </Link>
                <Link
                    underline="hover"
                    color="inherit"
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        navigate(`/buildings/${id}`);
                    }}
                >
                    Building Details
                </Link>
                <Typography color="text.primary">Optimization Results</Typography>
            </Breadcrumbs>

            {/* Title and Subtitle with styled box */}
            <Box sx={{
                mb: 4,
                p: 4,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #f5f9f6 0%, #e8f5e8 100%)',
                border: '1px solid',
                borderColor: 'divider'
            }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
                    Building Optimization
                </Typography>
                <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 800 }}>
                    Based on your building's characteristics and available resources, here are our recommended
                    decarbonization actions to maximize energy efficiency and reduce carbon emissions.
                </Typography>
            </Box>

            {/* Optimization Cards Grid */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {optimizationCategories.map(category => renderOptimizationCard(category))}
            </Grid>

            {/* Charts Section */}
            {optimizationData?.charts && (
                <Box sx={{ mb: 4 }}>
                    <Box sx={{
                        mb: 4,
                        p: 4,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #f5f9f6 0%, #e8f5e8 100%)',
                        border: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
                            Before vs After Comparison
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 800 }}>
                            Visualize the impact of our recommended decarbonization actions on your building's energy consumption,
                            carbon emissions, and operational costs.
                        </Typography>
                    </Box>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Card sx={{ p: 2 }}>
                                <ReactECharts option={getChartOptions('energy', 'Energy Consumption')} style={{ height: '300px' }} />
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card sx={{ p: 2 }}>
                                <ReactECharts option={getChartOptions('emissions', 'CO₂ Emissions')} style={{ height: '300px' }} />
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card sx={{ p: 2 }}>
                                <ReactECharts option={getChartOptions('costs', 'Energy Costs')} style={{ height: '300px' }} />
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* Summary Cards */}
            {optimizationData?.emissions && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={optimizationData.energy_consumption.breakdown.pv_offset > 0 ? 2.4 : 3}>
                        <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                {optimizationData.emissions.savings_percentage.toFixed(1)}%
                            </Typography>
                            <Typography variant="body1">CO₂ Reduction</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={optimizationData.energy_consumption.breakdown.pv_offset > 0 ? 2.4 : 3}>
                        <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                {optimizationData.energy_cost.savings_percentage.toFixed(1)}%
                            </Typography>
                            <Typography variant="body1">Cost Savings</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={optimizationData.energy_consumption.breakdown.pv_offset > 0 ? 2.4 : 3}>
                        <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                €{optimizationData.installation_cost.total.toLocaleString()}
                            </Typography>
                            <Typography variant="body1">Total Renovation Cost</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={optimizationData.energy_consumption.breakdown.pv_offset > 0 ? 2.4 : 3}>
                        <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText' }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                {optimizationData.energy_consumption.savings_percentage.toFixed(1)}%
                            </Typography>
                            <Typography variant="body1">Energy Savings</Typography>
                        </Card>
                    </Grid>
                    {optimizationData.energy_consumption.breakdown.pv_offset > 0 && (
                        <Grid item xs={12} md={2.4}>
                            <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'secondary.light', color: 'white' }}>
                                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                    {optimizationData.energy_consumption.breakdown.pv_offset.toLocaleString()}
                                </Typography>
                                <Typography variant="body1">PV Production (kWh)</Typography>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            )}
        </Container>
    );
};

export default BuildingOptimization;            